// ===================================
// FORGOT PASSWORD PAGE
// frontend/src/pages/ForgotPassword.js
// Request password reset email
// ===================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      
      if (response.data.success) {
        setSuccess(true);
        setCountdown(60); // 60 seconds before can resend
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait before trying again.');
        setCountdown(60);
      } else {
        setError(err.response?.data?.message || 'Failed to send reset email');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setCountdown(60);
    } catch (err) {
      setError('Failed to resend. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        {/* Logo */}
        <div className="forgot-password-logo">
          <Link to="/">
            <span className="logo-icon">🎯</span>
            <span className="logo-text">Career-Coach.AI</span>
          </Link>
        </div>

        {!success ? (
          // Request Form
          <>
            <div className="forgot-password-icon">🔐</div>
            <h1>Forgot your password?</h1>
            <p className="subtitle">
              No worries! Enter your email address and we'll send you a link to reset your password.
            </p>

            {/* Error Message */}
            {error && (
              <div className="forgot-password-error">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="forgot-password-form">
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
          </>
        ) : (
          // Success State
          <>
            <div className="success-icon">📬</div>
            <h1>Check your email</h1>
            <p className="subtitle">
              We've sent a password reset link to<br />
              <strong>{email}</strong>
            </p>

            <div className="success-info">
              <p>The link will expire in <strong>1 hour</strong>.</p>
              <p>Don't forget to check your spam folder!</p>
            </div>

            {/* Resend Option */}
            <div className="resend-section">
              {countdown > 0 ? (
                <p className="resend-timer">
                  Resend available in {countdown}s
                </p>
              ) : (
                <button
                  type="button"
                  className="resend-link"
                  onClick={handleResend}
                  disabled={loading}
                >
                  Didn't receive it? Resend email
                </button>
              )}
            </div>

            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
            >
              Try a different email
            </button>
          </>
        )}

        <div className="back-to-login">
          <Link to="/login">← Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;